/**
 * Renders a single configurable form section (grouped, ordered fields plus an
 * optional additional-info panel) for the medical PA form.
 *
 * @module medicalPaForm/components/medicalPaFormSection
 */
import RenderFields from "../../../components/formFields/renderFields";
import RenderSectionHeader from "../../../components/formFields/renderSectionHeader";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { FormField } from "../../../data-model/pharmaPaFormModel";

/** Props for {@link MedicalPaFormSection}. */
export interface MedicalPaFormSectionProps {
  /** Section definition from the form configuration. */
  section: FormField;
  /** Invoked with the section key when the section is clicked. */
  onSectionClick?: (sectionKey: string) => void;
  /** Working copy of the order used to fill field values. */
  data: MedicalPaOrder | null;
  /** Pristine order copy used to detect field-level changes. */
  originalData: MedicalPaOrder | null;
  /** Appends a blank entry to a repeatable array field. */
  onAddMore: (key: string) => void;
  /** Removes an entry from a repeatable array field. */
  onDelete: (path: string, parentKey: string) => void;
}

/**
 * Renders the section, grouping its fields by row index and sorting by order.
 *
 * @param props - See {@link MedicalPaFormSectionProps}.
 * @returns The section element, or `null` when the node is not a section.
 */
const MedicalPaFormSection = ({
  section,
  onSectionClick,
  data,
  originalData,
  onAddMore,
  onDelete,
}: MedicalPaFormSectionProps) => {
  if (section.type !== "section" || !("fields" in section)) {
    return null;
  }

  const groupedFields = section.fields.reduce<Record<number, FormField[]>>(
    (acc, field) => {
      const rowIndex = field.rowIndex ?? 0;
      if (!acc[rowIndex]) {
        acc[rowIndex] = [];
      }
      acc[rowIndex].push(field);
      return acc;
    },
    {},
  );

  return (
    <div
      key={section.key}
      id={`${section.key}`}
      className="single-section__container mb-6"
      onClick={(e) => {
        e.stopPropagation();
        onSectionClick?.(section.key);
      }}
    >
      <RenderSectionHeader
        sectionTitle={section.label}
        sectionKey={`${section.key}`}
        id={`${section.key}`}
      />
      <div>
        {Object.keys(groupedFields).map((rowIndex) => (
          <div key={rowIndex} className="single-row__container mb-2 flex gap-2">
            {groupedFields[rowIndex]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((field) => (
                <RenderFields
                  field={field}
                  key={field.key}
                  data={data}
                  originalData={originalData}
                  onAddMore={onAddMore}
                  onDelete={onDelete}
                  shouldEmitLogEvent={true}
                />
              ))}
          </div>
        ))}
      </div>
      {(section.additionalInfoHeader || section.additionalInfoContent) && (
        <div className="section-additonal-info flex w-full flex-col gap-2 bg-secondaryYellow-11 p-3">
          <div className="section-additonal-info__header text-tiny font-bold">
            {section.additionalInfoHeader ?? ""}
          </div>
          <div className="section-additonal-info__content text-[0.8125rem] font-normal">
            {section.additionalInfoContent ?? ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalPaFormSection;
